import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import { triplePSDatasource } from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { ClosedDay } from "../../models";
import { logout } from "../Auth/authSlice";
import { RootState } from "../../app/store";

export type ClosedDayFilter = "upcoming" | "past" | "all";

export interface ClosedDaysState {
  getClosedDays: APIResult<ClosedDay[]>;
  createClosedDay: APIResult<ClosedDay>;
  deleteClosedDay: APIResult<string>;

  closedDays: ClosedDay[];
  filter: ClosedDayFilter;
}

const initialState: ClosedDaysState = {
  getClosedDays: emptyAPIResult(),
  createClosedDay: emptyAPIResult(),
  deleteClosedDay: emptyAPIResult(),

  closedDays: [],
  filter: "upcoming",
};

export const getClosedDays = createAsyncThunk(
  "closedDays/getClosedDays",
  async (filter: ClosedDayFilter = "upcoming") => {
    return triplePSDatasource.getClosedDays(filter);
  }
);

export const createClosedDay = createAsyncThunk(
  "closedDays/createClosedDay",
  async ({ date, reason }: { date: string; reason: string }, thunkAPI) => {
    const created = await triplePSDatasource.createClosedDay(date, reason);
    // Refetch in the admin's current view so the new row shows (or correctly
    // doesn't, e.g. a past date while viewing "upcoming") with its server id.
    const filter = (thunkAPI.getState() as RootState).closedDays.filter;
    await thunkAPI.dispatch(getClosedDays(filter));
    return created;
  }
);

export const deleteClosedDay = createAsyncThunk(
  "closedDays/deleteClosedDay",
  async (id: string, thunkAPI) => {
    await triplePSDatasource.deleteClosedDay(id);
    const filter = (thunkAPI.getState() as RootState).closedDays.filter;
    await thunkAPI.dispatch(getClosedDays(filter));
    return id;
  }
);

export const closedDaysSlice = createSlice({
  name: "closedDays",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getClosedDays.pending, (state, action) => {
        state.getClosedDays = loadingAPIResult();
        if (action.meta.arg) state.filter = action.meta.arg;
      })
      .addCase(getClosedDays.fulfilled, (state, action) => {
        state.getClosedDays = successAPIResult(action.payload);
        state.closedDays = action.payload;
      })
      .addCase(getClosedDays.rejected, (state, action) => {
        state.getClosedDays = errorAPIResult(action.error);
      })
      .addCase(createClosedDay.pending, (state) => {
        state.createClosedDay = loadingAPIResult();
      })
      .addCase(createClosedDay.fulfilled, (state, action) => {
        state.createClosedDay = successAPIResult(action.payload);
      })
      .addCase(createClosedDay.rejected, (state, action) => {
        state.createClosedDay = errorAPIResult(action.error);
      })
      .addCase(deleteClosedDay.pending, (state) => {
        state.deleteClosedDay = loadingAPIResult();
      })
      .addCase(deleteClosedDay.fulfilled, (state, action) => {
        state.deleteClosedDay = successAPIResult(action.payload);
      })
      .addCase(deleteClosedDay.rejected, (state, action) => {
        state.deleteClosedDay = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getClosedDays = initialState.getClosedDays;
        state.createClosedDay = initialState.createClosedDay;
        state.deleteClosedDay = initialState.deleteClosedDay;
        state.closedDays = initialState.closedDays;
        state.filter = initialState.filter;
      });
  },
});

export const selectClosedDays = (state: RootState) =>
  state.closedDays.closedDays;
export const selectGetClosedDaysResult = (state: RootState) =>
  state.closedDays.getClosedDays;
export const selectCreateClosedDayResult = (state: RootState) =>
  state.closedDays.createClosedDay;

export default closedDaysSlice.reducer;
